import { useContext, useLayoutEffect, useState } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { GridLayoutLegacy, Loader } from '@elfsight/widget-ui';
import { getColorWithAlpha, useBreakpoints } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import ReactPaginate from 'react-paginate';
import {
  classnamePrefix,
  MOBILE_WIDTH,
  TABLET_WIDTH_LARGE
} from '../../../constants';
import { meta } from '../../../meta';
import { dictionary } from '../../dictionary';
import { processSource } from '../../helpers';
import { sourceContext } from '../../SourceContext';
import { LoadMore } from '../common';
import { Header } from '../common/header';
import { currentVideoContext } from '../currentVideoContext';
import { Player, VideoCard } from '../videos/video';
import { Info } from '../videos/video/info';

export const GridLayout = ({
  hasNext,
  loadNext,
  loading,
  nextVideo,
  data,
  widgetWidth,
  mobile = false
}) => {
  const { currentVideo } = useContext(currentVideoContext);
  const { videosSource } = useContext(sourceContext);
  const {
    playMode,
    headerVisible,
    gridColumns,
    gridGutter,
    gridRows,
    gridColumnsTablet,
    gridRowsTablet,
    gridColumnsMobile,
    gridRowsMobile,
    gridPagination,
    textColor,
    breakpoints,
    lang,
    playerElements
  } = useSettings();

  const [columnsCount, setColumnsCount] = useState(gridColumns);
  const [rowsCount, setRowsCount] = useState(gridRows);
  const [itemsPerPage, setItemsPerPageCount] = useState(
    columnsCount * rowsCount
  );
  const [{ type }] = useState(processSource({ source: videosSource }));
  const [cursor, setCursor] = useState(0);
  const [containerBreakpoints, containerRef] = useBreakpoints(
    MOBILE_WIDTH,
    TABLET_WIDTH_LARGE,
    ...breakpoints
  );
  const renderItems = [];

  const isMatchesBreakpoints380 = containerBreakpoints[380];

  useLayoutEffect(() => {
    if (widgetWidth <= MOBILE_WIDTH) {
      setColumnsCount(gridColumnsMobile || gridColumns);
      setRowsCount(gridRowsMobile || gridRows);
    } else if (widgetWidth <= TABLET_WIDTH_LARGE) {
      setColumnsCount(gridColumnsTablet || gridColumns);
      setRowsCount(gridRowsTablet || gridRows);
    } else {
      setColumnsCount(gridColumns);
      setRowsCount(gridRows);
    }

    setItemsPerPageCount(
      columnsCount > 0 && rowsCount > 0 ? columnsCount * rowsCount : 1
    );
  });

  for (
    let i = gridPagination === 'pagination' ? cursor : 0;
    i < Math.min(cursor + itemsPerPage, data.length);
    i++
  ) {
    renderItems.push(
      <VideoCard
        data={data[i]}
        key={i}
        number={i + 1}
        count={data.length}
        mobile={mobile}
        showVimeoIcon
        empty={!data[i]}
      />
    );
  }

  useLayoutEffect(() => {
    const needToLoad = data.length <= cursor + itemsPerPage;
    const canLoadNext = !loading && hasNext;

    if (needToLoad && canLoadNext) {
      loadNext();
    }
  }, [data.length, cursor, itemsPerPage, loading, hasNext, loadNext]);

  return (
    <Component
      ref={containerRef}
      _textColor={textColor}
      _isMatchesBreakpoints380={isMatchesBreakpoints380}
    >
      <PaginationGlobalStyles />
      {headerVisible && type !== 'videos' && <Header />}

      <Container>
        {playMode === 'player' && currentVideo && (
          <Video>
            <PlayerWrapper>
              <Player item={currentVideo} nextVideo={nextVideo} />
            </PlayerWrapper>

            <Info data={currentVideo} elements={playerElements} />
          </Video>
        )}

        <Videos>
          <GridLayoutLegacy
            appAlias={meta.appAlias}
            columns={columnsCount}
            gutter={{
              v: gridGutter,
              h: gridGutter
            }}
          >
            {renderItems}
          </GridLayoutLegacy>

          {data.length > itemsPerPage && gridPagination === 'pagination' && (
            <ReactPaginate
              pageCount={
                itemsPerPage === 0 ? 0 : Math.ceil(data.length / itemsPerPage)
              }
              onPageChange={({ selected }) =>
                setCursor(selected * itemsPerPage)
              }
              previousClassName={`${classnamePrefix}-grid-prev`}
              nextClassName={`${classnamePrefix}-grid-next`}
              containerClassName={`${classnamePrefix}-grid-pagination`}
              pageClassName={`${classnamePrefix}-paginationItem`}
              activeClassName={`${classnamePrefix}-grid-active`}
              breakClassName={`${classnamePrefix}-grid-break`}
              pageRangeDisplayed={!isMatchesBreakpoints380 ? 3 : 2}
              marginPagesDisplayed={1}
              disabledClassName={
                !hasNext ? `${classnamePrefix}-grid-disable` : ''
              }
              nextLabel={
                !loading ? (
                  dictionary[lang]?.['Next'] || dictionary.en['Next']
                ) : (
                  <Loader
                    size={isMatchesBreakpoints380 ? 15 : 20}
                    color={textColor}
                    className={`${classnamePrefix}-spinner-component`}
                  />
                )
              }
            />
          )}

          {gridPagination === 'button' && (
            <Load>
              {renderItems.length < data.length && (
                <LoadMore onClick={() => setCursor(cursor + itemsPerPage)} />
              )}

              {hasNext && loading && renderItems.length >= data.length && (
                <Loader
                  size={20}
                  color={textColor}
                  className={`${classnamePrefix}-spinner-component`}
                />
              )}
            </Load>
          )}
        </Videos>
      </Container>
    </Component>
  );
};

// for ReactPaginate
const PaginationGlobalStyles = createGlobalStyle`
  .${classnamePrefix}-grid-pagination {
    display: flex;
    margin: 24px -4px 0;
    list-style: none;
    justify-content: center;
    padding: 0;
  }

  .${classnamePrefix}-grid-prev {
    display: none;
  }

  .${classnamePrefix}-grid-next a {
    user-select: none;
    font-size: 13px;
    line-height: 20px;
    color: rgba(26, 46, 59, 0.5);
    padding: 4px 12px;
    border-radius: 2px;
    border: solid 1px rgba(26, 46, 59, 0.2);
    margin: 0 4px;
    cursor: pointer;
    text-decoration: none !important;
    display: block;
    transition: 0.3s ease background-color;

    &:hover {
      background-color: rgba(26, 46, 59, 0.06);
    }
  }

  .${classnamePrefix}-grid-active a {
    user-select: none;
  }

  .${classnamePrefix}-grid-break {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .${classnamePrefix}-grid-break a {
    user-select: none;
    font-size: 13px;
    line-height: 20px;
    color: rgb(26, 46, 59);
    pointer-events: none;
    display: block;
    margin: 0 4px;
  }

  .${classnamePrefix}-paginationItem a {
    user-select: none;
    font-size: 13px;
    line-height: 20px;
    color: rgba(26, 46, 59, 0.5);
    padding: 4px 12px;
    border-radius: 2px;
    border: solid 1px rgba(26, 46, 59, 0.2);
    margin: 0 4px;
    cursor: pointer;
    text-decoration: none !important;
    display: block;
    background-color: transparent;
    transition: 0.3s ease background-color;

    &:hover {
      background-color: rgba(26, 46, 59, 0.06);
    }
  }

  .${classnamePrefix}-grid-disable {
    display: none;
  }

`;

const Component = styled.div.attrs({
  className: `${classnamePrefix}-grid-component`
})`
  width: 100%;

  & .${classnamePrefix}-grid-item {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
  }

  & .${classnamePrefix}-grid-pagination a {
    color: ${({ _textColor }) => _textColor || 'rgba(26, 46, 59, 0.5)'};
    ${({ _textColor }) =>
      _textColor &&
      css`
        border-color: ${getColorWithAlpha(_textColor, 0.06)};
      `}
    ${({ _isMatchesBreakpoints380 }) =>
      _isMatchesBreakpoints380 &&
      css`
        font-size: 11px;
        line-height: 15px;
        padding: 2px 8px;
      `}

    &:hover {
      background-color: ${({ _textColor }) =>
        getColorWithAlpha(_textColor, 0.06) || 'rgba(26, 46, 59, 0.06)'};
    }
  }

  & .${classnamePrefix}-grid-active a {
    background-color: ${({ _textColor }) =>
      getColorWithAlpha(_textColor, 0.06) || 'rgba(26, 46, 59, 0.06)'};
    border-color: ${({ _textColor }) =>
      getColorWithAlpha(_textColor, 0.06) || 'rgba(26, 46, 59, 0.06)'};
    color: ${({ _textColor }) => _textColor || 'rgb(26, 46, 59)'};
  }

  & .${classnamePrefix}-grid-break a {
    font-size: 13px;
    line-height: 20px;
    user-select: none;
    pointer-events: none;
    display: block;
    margin: 0 4px;
    color: ${({ _textColor }) => _textColor || 'rgb(26, 46, 59)'};
    ${({ _isMatchesBreakpoints380 }) =>
      _isMatchesBreakpoints380 &&
      css`
        font-size: 11px;
        line-height: 15px;
        padding: 0;
      `}
  }

  & .${classnamePrefix}-grid-prev {
    display: none;
  }

  & .${classnamePrefix}-grid-disable {
    display: none;
  }
`;

const Video = styled.div.attrs({
  className: `${classnamePrefix}-grid-video`
})`
  margin-bottom: 24px;
`;

const Videos = styled.div.attrs({
  className: `${classnamePrefix}-grid-videos`
})`
  overflow: hidden;
`;

const PlayerWrapper = styled.div.attrs({
  className: `${classnamePrefix}-grid-player`
})``;

const Load = styled.div.attrs({
  className: `${classnamePrefix}-grid-load`
})`
  margin: 24px auto 0;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 500px;
  width: 100%;
`;

const Container = styled.div.attrs({
  className: `${classnamePrefix}-grid-container`
})`
  max-width: 100%;
  width: 100%;
  transition: 0.3s ease opacity;
`;
